import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Pencil,
  Save,
  Download,
  Mail,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Copy,
  Check,
  Bell,
  Calendar,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  formatMoney,
  useAddInvoiceLine,
  useInvoice,
  useIssueInvoice,
  useRemoveInvoiceLine,
  useRevertInvoiceToIssued,
  useUpdateInvoiceDraft,
  useUpdateInvoiceLine,
  useVoidInvoice,
  useCancelInvoice,
  useInvoiceEmails,
  useRenderInvoicePdf,
  useSendInvoiceEmail,
  useInvoiceReminders,
  useSendInvoiceReminder,
  useRecordPayment,
  useRefundInvoice,
  useInvoiceCreditNotes,
  type InvoiceLineRow,
} from '@/hooks/useInvoices';
import { Button } from '@/components/ui/Button';
import { PageLayout } from '@/components/ui/PageLayout';
import { EntityTasksPanel } from '@/components/tasks/EntityTasksPanel';
import {
  PAGE_TITLE,
  STATUS_BADGE,
  TABLE_CELL,
  TABLE_CELL_MUTED,
  TABLE_HEADER,
} from '@/components/ui/pageChrome';
import { FORM_LABEL_TYPE, TYPE, UI_MUTED, UI_SECONDARY, UI_SECTION } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Skeleton } from '@/components/ui/Skeleton';
import { InvoiceStatusBadges } from './Invoices';
import { AppointmentLinePicker } from '@/components/invoices/AppointmentLinePicker';
import { CreditNoteLinks } from '@/components/invoices/CreditNoteLinks';
import { PaymentManagement } from '@/components/invoices/PaymentManagement';
import { usePatient } from '@/hooks/usePatients';
import { formatPatientTelephoneNumbers } from '@/lib/patientStamkortFields';
import {
  useCreatePayCheckout,
  useRequestClinioPayLink,
  useRequestRefund,
  useClinicPayStatus,
  deriveClinicPayDisplayStatus,
  buildPayCheckoutReturnUrls,
  payReturnStorageKey,
} from '@/hooks/useClinicPay';
import { supabase } from '@/lib/supabase';
import {
  useClinicSdEnabled,
  useValidateInvoice,
  useSubmitInvoice,
} from '@/hooks/useSygeforsikring';
import { useClinic } from '@/hooks/useClinicSettings';
import { VatMomsField } from '@/components/ui/VatMomsField';
import { clinicVatRatePercent, itemIncludesVat, itemVatRatePercent } from '@/lib/vat';
import { ManualPaymentFields } from '@/components/invoices/ManualPaymentFields';
import { MobilePayRequestConfirm } from '@/components/payments/MobilePayRequestConfirm';
import {
  useInvoiceMobilePayRequest,
  useMobilePayRequestGate,
} from '@/hooks/useInvoiceMobilePayRequest';
import {
  isPaymentDateOnOrBeforeToday,
  isPaymentRequestMethod,
  paymentDateInputToIso,
  recordedPaymentMethod,
  todayDateInputValue,
} from '@/lib/manualPaymentMethods';
import {
  defaultRefundCents,
  invoiceHeaderActions,
  isCollectableInvoiceStatus,
} from '@/lib/invoiceDetailActions';
import { mobilePayRequestBlock } from '@/lib/mobilePayRequest';

interface LineDraft {
  description: string;
  quantity: number;
  unit_price_cents: number;
  vat_rate_percent: number;
  discount_pct: number;
}

const EMPTY_LINE: LineDraft = {
  description: '',
  quantity: 1,
  unit_price_cents: 0,
  vat_rate_percent: 25,
  discount_pct: 0,
};

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const { data: invoice, isLoading, error } = useInvoice(id);
  const { data: creditNotes } = useInvoiceCreditNotes(id);
  const updateDraft = useUpdateInvoiceDraft();
  const addLine = useAddInvoiceLine();
  const updateLine = useUpdateInvoiceLine();
  const removeLine = useRemoveInvoiceLine();
  const issue = useIssueInvoice();
  const recordPayment = useRecordPayment();
  const revert = useRevertInvoiceToIssued();
  const voidInv = useVoidInvoice();
  const cancelInv = useCancelInvoice();
  const refundLedger = useRefundInvoice();
  const renderPdf = useRenderInvoicePdf();
  const sendEmail = useSendInvoiceEmail();
  const { data: emailHistory } = useInvoiceEmails(id);
  const { data: reminderHistory } = useInvoiceReminders(id);
  const sendReminder = useSendInvoiceReminder();
  const createCheckout = useCreatePayCheckout();
  const requestPayLink = useRequestClinioPayLink();
  const requestMobilePay = useInvoiceMobilePayRequest();
  const mobilePayGate = useMobilePayRequestGate(activeClinicId);
  const { data: payStatus } = useClinicPayStatus(activeClinicId);
  const { data: clinic } = useClinic(activeClinicId);
  const clinicVatRate = clinicVatRatePercent(clinic);

  // SD: load clinic SD config + mutations
  const { data: sdInfo } = useClinicSdEnabled(activeClinicId);
  const validateInvoice = useValidateInvoice();
  const submitInvoice = useSubmitInvoice();
  const [showSdModal, setShowSdModal] = useState(false);
  const [sdValidation, setSdValidation] = useState<{
    valid: boolean;
    errors: Array<{ field?: string; message?: string }>;
  } | null>(null);

  const [dueAt, setDueAt] = useState('');
  const [notes, setNotes] = useState('');
  const [showAddLine, setShowAddLine] = useState(false);
  const [showAppointmentPicker, setShowAppointmentPicker] = useState(false);
  const [newLine, setNewLine] = useState<LineDraft>(EMPTY_LINE);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editLine, setEditLine] = useState<LineDraft>(EMPTY_LINE);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentOther, setPaymentOther] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => todayDateInputValue());
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [showPaidModal, setShowPaidModal] = useState(false);
  const [mobilePayConfirm, setMobilePayConfirm] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');

  const requestRefund = useRequestRefund();

  // Stripe pay state
  const [showPayLinkModal, setShowPayLinkModal] = useState(false);
  const [payLinkUrl, setPayLinkUrl] = useState('');
  const [payLinkCopied, setPayLinkCopied] = useState(false);
  const [showReminderHistory, setShowReminderHistory] = useState(false);

  // Clinio Pay refund state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmountDkk, setRefundAmountDkk] = useState('');
  const [refundReason, setRefundReason] = useState('requested_by_customer');

  const canAdmin = useMemo(
    () => Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId))),
    [activeClinicId, isPlatformAdmin, isClinicAdmin],
  );

  const isPayActive = deriveClinicPayDisplayStatus(payStatus) === 'active';

  useEffect(() => {
    if (!invoice) return;
    setDueAt(invoice.due_at ? invoice.due_at.slice(0, 10) : '');
    setNotes(invoice.notes ?? '');
  }, [invoice]);

  // If patient paid online but sync was missed, pick it up when the clinic opens the invoice.
  useEffect(() => {
    const payToken = (invoice as { pay_token?: string | null } | undefined)?.pay_token;
    if (!invoice || !isCollectableInvoiceStatus(invoice.status) || !payToken || payStatus?.status !== 'active') {
      return;
    }

    const invoiceId = invoice.id;

    let cancelled = false;
    async function syncPayStatus() {
      try {
        const { data, error } = await supabase.functions.invoke('pay-checkout-status', {
          body: { pay_token: payToken },
        });
        if (cancelled || error) return;

        const result = data as { ok?: boolean; data?: { status?: string } } | null;
        if (result?.ok && result.data?.status === 'succeeded') {
          void qc.invalidateQueries({ queryKey: ['invoices', invoiceId] });
        }
      } catch {
        // Non-fatal — clinic can refresh later
      }
    }

    void syncPayStatus();
    return () => {
      cancelled = true;
    };
  }, [invoice, payStatus?.status, qc]);

  if (isLoading) {
    return (
      <PageLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="flex w-full flex-col gap-s3" aria-hidden>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      </PageLayout>
    );
  }
  if (error || !invoice) {
    return (
      <PageLayout>
        <ErrorAlert message={t('invoices.load_error')} />
      </PageLayout>
    );
  }

  const isDraft = invoice.status === 'draft';
  const isVoid = invoice.status === 'void';
  const isCreditNote = invoice.kind === 'credit_note';
  const shownTotalCents = isCreditNote ? -Math.abs(invoice.total_cents) : invoice.total_cents;
  const headerActions = invoiceHeaderActions(invoice.status, invoice.kind);

  const handleCreditNote = async () => {
    if (!invoice || !confirm(t('invoices.credit_confirm'))) return;
    const { data, error } = await supabase.rpc('create_credit_note', {
      p_invoice_id: invoice.id,
    });
    if (error) {
      console.error('credit note failed', error);
      toast.error(t('invoices.credit_failed'));
      return;
    }
    const number =
      data && typeof data === 'object' && 'invoice_number' in data
        ? String((data as { invoice_number?: string }).invoice_number ?? '')
        : '';
    toast.success(t('invoices.credit_done', { number }));
    void qc.invalidateQueries({ queryKey: ['invoices', invoice.id] });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
  };
  const collectedCents = invoice.total_paid_cents ?? 0;
  const patientSnap = (invoice.patient_snapshot ?? null) as null | {
    first_name?: string;
    last_name?: string;
    email?: string | null;
    phone?: string | null;
    telephone_subscribers?: unknown;
    address?: Record<string, string | null> | null;
  };
  const patientSnapTelephones = formatPatientTelephoneNumbers(
    patientSnap?.telephone_subscribers,
    patientSnap?.phone,
  );
  const clinicSnap = (invoice.clinic_snapshot ?? null) as null | {
    name?: string;
    cvr?: string | null;
    invoice_prefix?: string;
    bank_info?: Record<string, unknown> | null;
  };

  async function handleDownloadPdf() {
    if (!invoice) return;
    try {
      const result = await renderPdf.mutateAsync(invoice.id);
      window.open(result.signed_url, '_blank');
    } catch (err) {
      console.error('render pdf failed', err);
      toast.error(t('invoices.pdf_error'));
    }
  }

  async function handlePayNow() {
    if (!invoice) return;
    try {
      const returnPath = `/invoices/${invoice.id}`;
      const result = await createCheckout.mutateAsync({
        invoice_id: invoice.id,
        ...buildPayCheckoutReturnUrls(returnPath),
      });
      if (result.pay_token) {
        sessionStorage.setItem(payReturnStorageKey(result.pay_token), returnPath);
      }
      window.location.href = result.checkout_url;
    } catch (err) {
      console.error('pay now failed', err);
      toast.error(t('payments.pay_error'));
    }
  }

  async function handleRequestPayLink() {
    if (!invoice) return;
    try {
      const result = await requestPayLink.mutateAsync(invoice.id);
      setPayLinkUrl(result.pay_url ?? `${window.location.origin}/pay-invoice/${result.pay_token}`);
      setPayLinkCopied(false);
      setShowPayLinkModal(true);
    } catch (err) {
      console.error('request pay link failed', err);
      toast.error(t('payments.pay_error'));
    }
  }

  function openRefund() {
    if (!invoice) return;
    const collected = invoice.total_paid_cents ?? 0;
    const cents = defaultRefundCents(invoice.total_cents, collected);
    setRefundAmountDkk((cents / 100).toFixed(2));
    setRefundReason(collected > invoice.total_cents ? 'duplicate' : 'requested_by_customer');
    setShowRefundModal(true);
  }

  async function handleRefund(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    const amountOre = Math.round(parseFloat(refundAmountDkk) * 100);
    const collected = invoice.total_paid_cents ?? 0;
    if (!Number.isFinite(amountOre) || amountOre <= 0 || amountOre > collected) {
      toast.error(t('payments.refund_invalid_amount'));
      return;
    }
    try {
      if (invoice.pay_token) {
        await requestRefund.mutateAsync({
          invoice_id: invoice.id,
          amount: amountOre,
          reason: refundReason,
        });
      } else {
        await refundLedger.mutateAsync({
          invoice_id: invoice.id,
          reason: refundReason,
          refund_amount_cents: amountOre,
        });
      }
      toast.success(t('payments.refund_success'));
      setShowRefundModal(false);
      setRefundAmountDkk('');
    } catch (err) {
      console.error('refund failed', err);
      toast.error(t('payments.refund_error'));
    }
  }

  async function handleCopyPayLink() {
    try {
      await navigator.clipboard.writeText(payLinkUrl);
      setPayLinkCopied(true);
      setTimeout(() => setPayLinkCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  }

  function openSendEmailModal() {
    setEmailRecipient(patientSnap?.email ?? '');
    setEmailSubject(`Faktura ${invoice?.invoice_number ?? ''} fra ${clinicSnap?.name ?? ''}`);
    setEmailMessage('');
    setShowSendEmailModal(true);
  }

  async function handleSendEmail(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await sendEmail.mutateAsync({
        invoice_id: invoice.id,
        recipient: emailRecipient.trim() || undefined,
        subject: emailSubject.trim() || undefined,
        message: emailMessage.trim() || undefined,
      });
      toast.success(t('invoices.email_sent_toast'));
      setShowSendEmailModal(false);
    } catch (err) {
      console.error('send email failed', err);
      toast.error(t('invoices.email_failed_toast'));
    }
  }

  async function handleSendReminder(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await sendReminder.mutateAsync({ 
        invoice_id: invoice.id,
        custom_message: reminderMessage.trim() || undefined,
      });
      toast.success(t('invoices.reminder_sent_toast'));
      setShowReminderModal(false);
      setReminderMessage('');
    } catch (err) {
      console.error('send reminder failed', err);
      toast.error(t('invoices.reminder_failed_toast'));
    }
  }

  function openSendReminderModal() {
    setReminderMessage('');
    setShowReminderModal(true);
  }

  async function handleSaveDraft(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await updateDraft.mutateAsync({
        invoice_id: invoice.id,
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t('invoices.toast_saved'));
    } catch (err) {
      console.error('update draft failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  async function handleAddLine(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await addLine.mutateAsync({
        invoice_id: invoice.id,
        description: newLine.description.trim(),
        quantity: newLine.quantity,
        unit_price_cents: newLine.unit_price_cents,
        vat_rate_percent: itemVatRatePercent(itemIncludesVat(newLine.vat_rate_percent), clinicVatRate),
        discount_pct: newLine.discount_pct || undefined,
      });
      toast.success(t('invoices.toast_line_added'));
      setNewLine({ ...EMPTY_LINE, vat_rate_percent: clinicVatRate });
      setShowAddLine(false);
    } catch (err) {
      console.error('add line failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  function startEditLine(line: InvoiceLineRow) {
    setEditingLineId(line.id);
    setEditLine({
      description: line.description,
      quantity: Number(line.quantity),
      unit_price_cents: line.unit_price_cents,
      vat_rate_percent: Number(line.vat_rate_percent),
      discount_pct: line.discount_pct ? Number(line.discount_pct) : 0,
    });
  }

  async function handleUpdateLine(lineId: string, e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await updateLine.mutateAsync({
        line_id: lineId,
        invoice_id: invoice.id,
        description: editLine.description.trim(),
        quantity: editLine.quantity,
        unit_price_cents: editLine.unit_price_cents,
        vat_rate_percent: itemVatRatePercent(itemIncludesVat(editLine.vat_rate_percent), clinicVatRate),
        discount_pct: editLine.discount_pct,
        clear_discount: editLine.discount_pct === 0,
      });
      toast.success(t('invoices.toast_line_updated'));
      setEditingLineId(null);
    } catch (err) {
      console.error('update line failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  async function handleRemoveLine(line: InvoiceLineRow) {
    if (!invoice) return;
    if (!confirm(t('invoices.lines_remove') + '?')) return;
    try {
      await removeLine.mutateAsync({ line_id: line.id, invoice_id: invoice.id });
      toast.success(t('invoices.toast_line_removed'));
    } catch (err) {
      console.error('remove line failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  async function handleIssue() {
    if (!invoice) return;
    if (!confirm(t('invoices.confirm_issue'))) return;
    try {
      await issue.mutateAsync(invoice.id);
      toast.success(t('invoices.toast_issued'));
    } catch (err) {
      console.error('issue failed', err);
      toast.error(t('invoices.issue_error'));
    }
  }

  function openMarkPaid() {
    setPaymentDate(todayDateInputValue());
    setShowPaidModal(true);
  }

  async function handleMarkPaid(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;

    if (isPaymentRequestMethod(paymentMethod)) {
      setMobilePayConfirm(true);
      return;
    }

    const method = recordedPaymentMethod(paymentMethod, paymentOther);
    if (!method) {
      toast.error(t('invoices.payment_method_required'));
      return;
    }
    if (!isPaymentDateOnOrBeforeToday(paymentDate)) {
      toast.error(t('invoices.payment_date_invalid'));
      return;
    }

    const remainingBalance = invoice.total_cents - (invoice.total_paid_cents ?? 0);

    try {
      await recordPayment.mutateAsync({
        invoice_id: invoice.id,
        amount_cents: remainingBalance,
        payment_date: paymentDateInputToIso(paymentDate),
        payment_method: method,
        payment_reference: paymentReference.trim() || undefined,
        notes: paymentNotes.trim() || undefined,
      });
      toast.success(t('invoices.toast_paid'));
      setShowPaidModal(false);
      setPaymentMethod('');
      setPaymentOther('');
      setPaymentDate(todayDateInputValue());
      setPaymentReference('');
      setPaymentNotes('');
    } catch (err) {
      console.error('mark paid failed', err);
      toast.error(t('invoices.pay_error'));
    }
  }

  async function handleVoid(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    const reason = voidReason.trim();
    const cancelInstead = invoiceHeaderActions(invoice.status).annulMode === 'cancel';
    try {
      if (cancelInstead) {
        await cancelInv.mutateAsync({
          invoice_id: invoice.id,
          reason,
        });
        toast.success(t('invoices.cancelled'));
      } else {
        await voidInv.mutateAsync({
          invoice_id: invoice.id,
          reason,
        });
        toast.success(t('invoices.toast_voided'));
      }
      setShowVoidModal(false);
      setVoidReason('');
    } catch (err) {
      console.error('void failed', err);
      toast.error(cancelInstead ? t('invoices.cancel_error') : t('invoices.void_error'));
    }
  }

  async function handleRevert(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await revert.mutateAsync({
        invoice_id: invoice.id,
        reason: revertReason.trim(),
      });
      toast.success(t('invoices.toast_reverted'));
      setShowRevertModal(false);
      setRevertReason('');
    } catch (err) {
      console.error('revert failed', err);
      toast.error(t('invoices.revert_error'));
    }
  }

  return (
    <PageLayout contentClassName="space-y-s5">
      <div className="space-y-s5">
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/economy/invoices')}
              className={cn('inline-flex items-center gap-s1 text-ds-teal hover:text-ds-teal-700', TYPE.body)}
            >
              <ArrowLeft className="h-4 w-4" />
              {t('invoices.back_to_list')}
            </button>
            {invoice.patient_id && (
              <button
                type="button"
                onClick={() => navigate(`/patients/${invoice.patient_id}`)}
                className={cn('inline-flex items-center gap-s1 text-ds-teal hover:text-ds-teal-700', TYPE.body)}
              >
                <ArrowLeft className="h-4 w-4" />
                {t('invoices.back_to_patient')}
              </button>
            )}
          </div>

          {/* Header card */}
          <section className="rounded-card bg-ds-card p-s5 shadow-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className={PAGE_TITLE}>
                    {isCreditNote ? `${t('invoices.kind_credit_note')} ` : ''}
                    {invoice.invoice_number ?? t('invoices.draft_label')}
                  </h1>
                  {isCreditNote ? (
                    <span className="inline-flex items-center rounded-full bg-ds-neutral-tint px-2.5 py-0.5 text-sm font-semibold text-ds-text">
                      {t('invoices.kind_credit_note')}
                    </span>
                  ) : (
                    <InvoiceStatusBadges
                      status={invoice.status}
                      lastSentAt={invoice.last_sent_at}
                      lastReminderSentAt={invoice.last_reminder_sent_at}
                      paymentMethod={invoice.payment_method}
                      totalPaidCents={invoice.total_paid_cents}
                    />
                  )}
                </div>
                {invoice.status === 'refunded' ? (
                  <p className={`mt-2 ${TYPE.body}`} data-refunded-invoice="">
                    {t('invoices.refunded_settled')}
                  </p>
                ) : null}
                <CreditNoteLinks notes={creditNotes ?? []} />
                {patientSnap ? (
                  <div className={`mt-1 ${UI_MUTED}`}>
                    <div>
                      {patientSnap.first_name} {patientSnap.last_name}
                    </div>
                    {patientSnapTelephones ? (
                      <div className={UI_SECONDARY}>{patientSnapTelephones}</div>
                    ) : null}
                  </div>
                ) : (
                  <PatientFallback patientId={invoice.patient_id} />
                )}
                {invoice.issued_at && (
                  <div className={`mt-1 ${UI_SECONDARY}`}>
                    {t('invoices.issued_at_label')}:{' '}
                    {new Date(invoice.issued_at).toLocaleDateString('da-DK')}
                  </div>
                )}
                {invoice.last_sent_at && (
                  <div className={`mt-1 ${TYPE.small} text-ds-teal`}>
                    {t('invoices.email_sent_at', {
                      date: new Date(invoice.last_sent_at).toLocaleString('da-DK'),
                    })}
                    {invoice.last_emailed_to && (
                      <>
                        {' '}
                        {t('invoices.email_sent_to', { email: invoice.last_emailed_to })}
                      </>
                    )}
                  </div>
                )}
                {(invoice.paid_at || (invoice.status === 'paid' && invoice.last_payment_at)) && (
                  <div className={`mt-1 ${TYPE.small} text-ds-green-ink`}>
                    {t('invoices.paid_at_label')}:{' '}
                    {new Date(invoice.paid_at ?? invoice.last_payment_at ?? '').toLocaleDateString('da-DK')}
                  </div>
                )}
                {invoice.voided_at && (
                  <div className={`mt-1 ${TYPE.small} text-ds-red-ink`}>
                    {t('invoices.voided_at_label')}:{' '}
                    {new Date(invoice.voided_at).toLocaleDateString('da-DK')}
                    {invoice.voided_reason && ` — ${invoice.voided_reason}`}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                {isDraft && (
                  <Button
                    onClick={handleIssue}
                    disabled={issue.isPending || invoice.lines.length === 0}
                  >
                    {issue.isPending ? t('common.saving') : t('invoices.actions_issue')}
                  </Button>
                )}
                {headerActions.markPaid && (
                  <Button onClick={openMarkPaid}>
                    {t('invoices.actions_mark_paid')}
                  </Button>
                )}
                {headerActions.payOnline && canAdmin && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={handlePayNow}
                      disabled={createCheckout.isPending}
                    >
                      {createCheckout.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                      <span className="ml-1">{t('payments.pay_now')}</span>
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleRequestPayLink}
                      disabled={requestPayLink.isPending || !isPayActive}
                      title={
                        !isPayActive
                          ? t('payments.pay_not_configured')
                          : undefined
                      }
                    >
                      {requestPayLink.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : null}
                      <span>{t('payments.send_pay_link')}</span>
                    </Button>
                  </>
                )}
                {headerActions.revertPaid && canAdmin && (
                  <Button variant="secondary" onClick={() => setShowRevertModal(true)}>
                    {t('invoices.actions_revert')}
                  </Button>
                )}
                {headerActions.refundOnline && canAdmin && collectedCents > 0 && (
                  <Button
                    variant="secondary"
                    onClick={openRefund}
                    disabled={requestRefund.isPending || refundLedger.isPending}
                  >
                    {requestRefund.isPending || refundLedger.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    <span className={requestRefund.isPending || refundLedger.isPending ? 'ml-1' : ''}>
                      {t('payments.refund_action')}
                    </span>
                  </Button>
                )}
                {headerActions.credit && canAdmin && (
                  <Button variant="secondary" onClick={() => void handleCreditNote()}>
                    {t('invoices.actions_credit')}
                  </Button>
                )}
                {headerActions.annulMode !== null && canAdmin && (
                  <Button variant="secondary" onClick={() => setShowVoidModal(true)}>
                    {t('invoices.actions_void')}
                  </Button>
                )}
                {headerActions.downloadPdf && (
                  <Button
                    variant="secondary"
                    onClick={handleDownloadPdf}
                    disabled={renderPdf.isPending}
                  >
                    {renderPdf.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    <span className="ml-1">{t('invoices.download_pdf')}</span>
                  </Button>
                )}
                {headerActions.sendEmail && canAdmin && (
                  <>
                    <Button onClick={openSendEmailModal}>
                      <Mail className="h-4 w-4" />
                      <span className="ml-1">{t('invoices.send_email')}</span>
                    </Button>
                    {headerActions.sendReminder && (
                      <Button
                        variant="secondary"
                        onClick={openSendReminderModal}
                        disabled={sendReminder.isPending}
                      >
                        {sendReminder.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Bell className="h-4 w-4" />
                        )}
                        <span className="ml-1">{t('invoices.send_reminder')}</span>
                      </Button>
                    )}
                  </>
                )}
                {/* Submit to Sygeforsikring Danmark — only for issued invoices not yet submitted */}
                {headerActions.submitToSygeforsikring && canAdmin && sdInfo?.enabled && !invoice.sd_uuid && (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      if (!activeClinicId || !id) return;
                      setSdValidation(null);
                      setShowSdModal(true);
                      try {
                        const r = await validateInvoice.mutateAsync({
                          clinic_id: activeClinicId,
                          invoice_id: id,
                        });
                        setSdValidation({ valid: r.valid, errors: r.errors });
                      } catch {
                        setSdValidation({
                          valid: false,
                          errors: [{ message: t('sd.validation_failed') }],
                        });
                      }
                    }}
                    disabled={validateInvoice.isPending}
                  >
                    {validateInvoice.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    <span className={validateInvoice.isPending ? 'ml-1' : ''}>
                      {t('sd.submit_to_sd')}
                    </span>
                  </Button>
                )}
                {/* Show SD UUID if already submitted */}
                {invoice.sd_uuid && (
                  <span className={cn('inline-flex items-center rounded-full bg-ds-green-tint px-3 py-1 text-ds-green-ink', STATUS_BADGE)}>
                    SD: {invoice.sd_uuid}
                  </span>
                )}
                {clinicSnap && (
                  <div className={`text-right ${UI_SECONDARY}`}>{clinicSnap.name}</div>
                )}
              </div>
            </div>
          </section>

          {invoice.clinic_id ? (
            <EntityTasksPanel
              clinicId={invoice.clinic_id}
              entityType="invoice"
              entityId={invoice.id}
            />
          ) : null}

          {/* Draft fields */}
          {isDraft && (
            <section className="rounded-card bg-ds-card p-s5 shadow-card">
              <form onSubmit={handleSaveDraft} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <DanishDateField
                    label={t('invoices.field_due_at')}
                    name="invoice-due-at"
                    value={dueAt}
                    onChange={setDueAt}
                  />
                </div>
                <Textarea
                  label={t('invoices.field_notes')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button type="submit" variant="secondary" loading={updateDraft.isPending} loadingText={t('common.saving')}>
                    {t('invoices.actions_save_draft')}
                  </Button>
                </div>
              </form>
            </section>
          )}

          {!isDraft && !isCreditNote && ((invoice.status !== 'paid' && invoice.due_at) || invoice.notes) && (
            <section className="rounded-card bg-ds-card p-s5 shadow-card">
              {invoice.status !== 'paid' && invoice.due_at && (
                <div className={TYPE.body}>
                  <span className="text-ds-muted">{t('invoices.field_due_at')}: </span>
                  <span className="font-medium text-ds-text">
                    {new Date(invoice.due_at).toLocaleDateString('da-DK')}
                  </span>
                </div>
              )}
              {invoice.notes && (
                <div className={cn('mt-2 whitespace-pre-wrap text-ds-text', TYPE.body)}>
                  {invoice.notes}
                </div>
              )}
            </section>
          )}

          {/* Lines */}
          <section className="rounded-card bg-ds-card p-s5 shadow-card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className={UI_SECTION}>
                {t('invoices.lines_title')}
              </h2>
              {isDraft && !showAddLine && (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setShowAppointmentPicker(true)}>
                    <Calendar className="mr-1 h-4 w-4" />
                    {t('invoices.appointments.add_from_appointment')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setNewLine({ ...EMPTY_LINE, vat_rate_percent: clinicVatRate });
                      setShowAddLine(true);
                    }}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    {t('invoices.lines_add')}
                  </Button>
                </div>
              )}
            </div>

            {invoice.lines.length === 0 && !showAddLine ? (
              <p className={cn('rounded-pop border border-dashed border-ds-line px-s4 py-s6 text-center', UI_MUTED)}>
                {t('invoices.lines_empty')}
              </p>
            ) : (
              <div className="overflow-hidden rounded-pop border border-ds-line">
                <table className="min-w-full divide-y divide-ds-line">
                  <thead>
                    <tr>
                      <Th>{t('invoices.line_field_description')}</Th>
                      <Th>{t('invoices.line_field_quantity')}</Th>
                      <Th>{t('invoices.line_field_unit_price')}</Th>
                      <Th>{t('invoices.line_field_vat')}</Th>
                      <Th>{t('invoices.totals_total')}</Th>
                      <th className="relative px-3 py-3">
                        <span className="sr-only">{t('invoices.col_actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ds-line bg-ds-card">
                    {invoice.lines.map((line) =>
                      editingLineId === line.id ? (
                        <tr key={line.id}>
                          <td colSpan={6} className="p-3">
                            <form
                              onSubmit={(e) => handleUpdateLine(line.id, e)}
                              className="grid grid-cols-1 gap-3 md:grid-cols-6"
                            >
                              <Input
                                label={t('invoices.line_field_description')}
                                value={editLine.description}
                                onChange={(e) =>
                                  setEditLine({ ...editLine, description: e.target.value })
                                }
                                className="md:col-span-2"
                                required
                              />
                              <Input
                                label={t('invoices.line_field_quantity')}
                                type="number"
                                step="0.01"
                                value={editLine.quantity.toString()}
                                onChange={(e) =>
                                  setEditLine({ ...editLine, quantity: Number(e.target.value) })
                                }
                              />
                              <Input
                                label={t('invoices.line_field_unit_price')}
                                type="number"
                                step="0.01"
                                value={(editLine.unit_price_cents / 100).toString()}
                                onChange={(e) =>
                                  setEditLine({
                                    ...editLine,
                                    unit_price_cents: Math.round(Number(e.target.value) * 100),
                                  })
                                }
                              />
                              <VatMomsField
                                includesVat={itemIncludesVat(editLine.vat_rate_percent)}
                                clinicRatePercent={clinicVatRate}
                                onChange={(includesVat) =>
                                  setEditLine({
                                    ...editLine,
                                    vat_rate_percent: itemVatRatePercent(includesVat, clinicVatRate),
                                  })
                                }
                              />
                              <Input
                                label={t('invoices.line_field_discount_pct')}
                                type="number"
                                value={editLine.discount_pct.toString()}
                                onChange={(e) =>
                                  setEditLine({
                                    ...editLine,
                                    discount_pct: Number(e.target.value),
                                  })
                                }
                              />
                              <div className="flex items-end gap-2 md:col-span-6">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setEditingLineId(null)}
                                >
                                  {t('common.cancel')}
                                </Button>
                                <Button type="submit" size="sm" disabled={updateLine.isPending}>
                                  <Save className="mr-1 h-4 w-4" />
                                  {t('invoices.lines_save')}
                                </Button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      ) : (
                        <tr key={line.id}>
                          <td className={cn('px-3 py-3', TABLE_CELL)}>
                            <div>
                              {line.description}
                              {line.source === 'appointment' && line.source_id && (
                                <p className={`mt-0.5 ${UI_SECONDARY}`}>
                                  {t('invoices.appointments.line_tag')}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className={cn('px-3 py-3', TABLE_CELL_MUTED)}>
                            {line.quantity}
                          </td>
                          <td className={cn('px-3 py-3', TABLE_CELL_MUTED)}>
                            {formatMoney(isCreditNote ? -Math.abs(line.unit_price_cents) : line.unit_price_cents, invoice.currency)}
                          </td>
                          <td className={cn('px-3 py-3', TABLE_CELL_MUTED)}>
                            {line.vat_rate_percent}%
                          </td>
                          <td className={cn('px-3 py-3 font-medium', TABLE_CELL)}>
                            {formatMoney(isCreditNote ? -Math.abs(line.line_total_cents) : line.line_total_cents, invoice.currency)}
                          </td>
                          <td className={cn('px-3 py-3 text-right', TABLE_CELL)}>
                            {isDraft && (
                              <div className="inline-flex gap-2">
                                <Button
                                  type="button"
                                  variant="tertiary"
                                  size="icon"
                                  onClick={() => startEditLine(line)}
                                  aria-label={t('common.edit')}
                                >
                                  <Pencil />
                                </Button>
                                <Button
                                  type="button"
                                  variant="danger"
                                  size="icon"
                                  onClick={() => handleRemoveLine(line)}
                                  aria-label={t('invoices.lines_remove')}
                                >
                                  <Trash2 />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {showAddLine && isDraft && (
              <form
                onSubmit={handleAddLine}
                className="mt-4 grid grid-cols-1 gap-3 rounded-md border border-ds-line bg-ds-sunk p-4 md:grid-cols-6"
              >
                <Input
                  label={t('invoices.line_field_description')}
                  value={newLine.description}
                  onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                  required
                  className="md:col-span-2"
                />
                <Input
                  label={t('invoices.line_field_quantity')}
                  type="number"
                  step="0.01"
                  value={newLine.quantity.toString()}
                  onChange={(e) => setNewLine({ ...newLine, quantity: Number(e.target.value) })}
                />
                <Input
                  label={t('invoices.line_field_unit_price')}
                  type="number"
                  step="0.01"
                  value={(newLine.unit_price_cents / 100).toString()}
                  onChange={(e) =>
                    setNewLine({
                      ...newLine,
                      unit_price_cents: Math.round(Number(e.target.value) * 100),
                    })
                  }
                />
                <VatMomsField
                  includesVat={itemIncludesVat(newLine.vat_rate_percent)}
                  clinicRatePercent={clinicVatRate}
                  onChange={(includesVat) =>
                    setNewLine({
                      ...newLine,
                      vat_rate_percent: itemVatRatePercent(includesVat, clinicVatRate),
                    })
                  }
                />
                <Input
                  label={t('invoices.line_field_discount_pct')}
                  type="number"
                  value={newLine.discount_pct.toString()}
                  onChange={(e) => setNewLine({ ...newLine, discount_pct: Number(e.target.value) })}
                />
                <div className="flex items-end gap-2 md:col-span-6">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowAddLine(false);
                      setNewLine({ ...EMPTY_LINE, vat_rate_percent: clinicVatRate });
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" size="sm" disabled={addLine.isPending}>
                    {addLine.isPending ? t('common.saving') : t('invoices.lines_add')}
                  </Button>
                </div>
              </form>
            )}

            <div className={cn('ml-auto mt-6 max-w-xs space-y-1', TYPE.body)}>
              <Row label={t('invoices.totals_subtotal')}>
                {formatMoney(isCreditNote ? -Math.abs(invoice.subtotal_cents) : invoice.subtotal_cents, invoice.currency)}
              </Row>
              {invoice.discount_cents > 0 && (
                <Row label={t('invoices.totals_discount')}>
                  −{formatMoney(invoice.discount_cents, invoice.currency)}
                </Row>
              )}
              <Row label={t('invoices.totals_vat')}>
                {formatMoney(isCreditNote ? -Math.abs(invoice.vat_cents) : invoice.vat_cents, invoice.currency)}
              </Row>
              <Row label={t('invoices.totals_total')} bold>
                {formatMoney(shownTotalCents, invoice.currency)}
              </Row>
            </div>
          </section>

          {/* Payment Management - shows for issued/partial/overdue/paid invoices */}
          {!isDraft && !isVoid && !isCreditNote && (
            <>
              <PaymentManagement
                invoice={invoice}
                canManage={canAdmin}
                onOpenRefund={openRefund}
              />
            </>
          )}

          {/* Modals */}
          {showPaidModal && (
            <Modal onClose={() => setShowPaidModal(false)} title={t('invoices.actions_mark_paid')}>
              {mobilePayConfirm && (
                <MobilePayRequestConfirm
                  amountLabel={formatMoney(
                    Math.max(0, invoice.total_cents - (invoice.total_paid_cents ?? 0)),
                    invoice.currency,
                  )}
                  patientName={
                    `${patientSnap?.first_name ?? ''} ${patientSnap?.last_name ?? ''}`.trim() ||
                    invoice.patient_id
                  }
                  block={mobilePayRequestBlock({
                    ready: mobilePayGate.ready,
                    mobilePayEnabled: mobilePayGate.mobilePayEnabled,
                    clinioPayAvailable: mobilePayGate.clinioPayAvailable,
                    outstandingCents: Math.max(0, invoice.total_cents - (invoice.total_paid_cents ?? 0)),
                  })}
                  ready={mobilePayGate.ready}
                  busy={requestMobilePay.isPending}
                  onCancel={() => {
                    setMobilePayConfirm(false);
                    setPaymentMethod('');
                  }}
                  onConfirm={() => {
                    void requestMobilePay
                      .mutateAsync(invoice.id)
                      .then(() => {
                        setMobilePayConfirm(false);
                        setPaymentMethod('');
                        toast.success(t('appointments.payment_choice.mobilepay_sent'));
                      })
                      .catch((err: unknown) => {
                        toast.error(
                          err instanceof Error
                            ? err.message
                            : t('appointments.payment_choice.errors.mobilepay_failed'),
                        );
                      });
                  }}
                />
              )}
              <form onSubmit={handleMarkPaid} className="space-y-4">
                <p className={UI_MUTED}>
                  {t('invoices.mark_paid_description', 'Mark this invoice as fully paid. For partial payments or detailed tracking, use the Payment Management section below.')}
                </p>
                <ManualPaymentFields
                  idPrefix="mark-paid"
                  method={paymentMethod}
                  onMethodChange={(next) => {
                    setPaymentMethod(next);
                    if (isPaymentRequestMethod(next)) setMobilePayConfirm(true);
                  }}
                  otherDetail={paymentOther}
                  onOtherDetailChange={setPaymentOther}
                  paymentDate={paymentDate}
                  onPaymentDateChange={setPaymentDate}
                />
                <Input
                  label={t('invoices.field_payment_reference')}
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder={t('invoices.payment_reference_placeholder', 'e.g., Transaction ID')}
                />
                <div>
                  <label className={`block ${FORM_LABEL_TYPE}`}>
                    {t('invoices.payment_notes', 'Notes')} ({t('common.optional', 'Optional')})
                  </label>
                  <textarea
                    rows={3}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    className={cn('mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-ds-text shadow-sm focus:border-ds-teal focus:outline-none focus:shadow-ring', TYPE.inputValue)}
                    placeholder={t('invoices.payment_notes_placeholder', 'Additional notes about this payment')}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={() => setShowPaidModal(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={recordPayment.isPending}>
                    {recordPayment.isPending
                      ? t('common.saving')
                      : isPaymentRequestMethod(paymentMethod)
                        ? t('invoices.mobilepay_request_send')
                        : t('invoices.actions_mark_paid')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showVoidModal && (
            <Modal onClose={() => setShowVoidModal(false)} title={t('invoices.actions_void')}>
              <form onSubmit={handleVoid} className="space-y-4">
                <p className={UI_MUTED}>
                  {t('invoices.confirm_void')}
                </p>
                <Input
                  label={t('invoices.field_void_reason')}
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  required
                />
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={() => setShowVoidModal(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={voidInv.isPending || cancelInv.isPending || !voidReason.trim()}>
                    {voidInv.isPending || cancelInv.isPending ? t('common.saving') : t('invoices.actions_void')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showRevertModal && (
            <Modal onClose={() => setShowRevertModal(false)} title={t('invoices.actions_revert')}>
              <form onSubmit={handleRevert} className="space-y-4">
                <p className={UI_MUTED}>
                  {t('invoices.confirm_revert')}
                </p>
                <Input
                  label={t('invoices.field_revert_reason')}
                  value={revertReason}
                  onChange={(e) => setRevertReason(e.target.value)}
                  required
                />
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowRevertModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={revert.isPending || !revertReason.trim()}>
                    {revert.isPending ? t('common.saving') : t('invoices.actions_revert')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showRefundModal && (
            <Modal onClose={() => setShowRefundModal(false)} title={t('payments.refund_title')}>
              <form onSubmit={handleRefund} className="space-y-4">
                <p className={UI_MUTED}>
                  {t('payments.refund_desc')}
                </p>
                <div>
                  <label className={`block ${FORM_LABEL_TYPE}`}>
                    {t('payments.refund_amount_label')}
                  </label>
                  <div className="mt-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={(collectedCents / 100).toFixed(2)}
                      value={refundAmountDkk}
                      onChange={(e) => setRefundAmountDkk(e.target.value)}
                      required
                      trailingIcon={<span className={UI_MUTED}>DKK</span>}
                    />
                  </div>
                </div>
                <div>
                  <label className={`block ${FORM_LABEL_TYPE}`}>
                    {t('payments.refund_reason_label')}
                  </label>
                  <select
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    className={cn('mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring', TYPE.inputValue)}
                  >
                    <option value="requested_by_customer">{t('payments.refund_reason_customer')}</option>
                    <option value="duplicate">{t('payments.refund_reason_duplicate')}</option>
                    <option value="fraudulent">{t('payments.refund_reason_fraudulent')}</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowRefundModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={requestRefund.isPending || refundLedger.isPending || !refundAmountDkk}
                  >
                    {requestRefund.isPending || refundLedger.isPending
                      ? t('common.saving')
                      : t('payments.refund_action')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showSendEmailModal && (
            <Modal onClose={() => setShowSendEmailModal(false)} title={t('invoices.send_email')}>
              <form onSubmit={handleSendEmail} className="space-y-4">
                <Input
                  label={t('invoices.email_recipient_label')}
                  type="email"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  required
                />
                <Input
                  label={t('invoices.email_subject_label')}
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  required
                />
                <div>
                  <label className={`block ${FORM_LABEL_TYPE}`}>
                    {t('invoices.email_message_label')}
                  </label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    rows={4}
                    className={cn('mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring', TYPE.inputValue)}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowSendEmailModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={sendEmail.isPending}>
                    {sendEmail.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    <span className="ml-1">
                      {sendEmail.isPending ? t('common.saving') : t('invoices.send_email')}
                    </span>
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showReminderModal && (
            <Modal onClose={() => setShowReminderModal(false)} title={t('invoices.send_reminder')}>
              <form onSubmit={handleSendReminder} className="space-y-4">
                <p className={UI_MUTED}>
                  {t('invoices.reminder_modal_desc')}
                </p>
                <div>
                  <label className={`block ${FORM_LABEL_TYPE}`}>
                    {t('invoices.reminder_custom_message_label')}
                  </label>
                  <textarea
                    value={reminderMessage}
                    onChange={(e) => setReminderMessage(e.target.value)}
                    rows={4}
                    placeholder={t('invoices.reminder_custom_message_placeholder')}
                    className={cn('mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring', TYPE.inputValue)}
                  />
                  <p className={`mt-1 ${UI_SECONDARY}`}>
                    {t('invoices.reminder_custom_message_hint')}
                  </p>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowReminderModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={sendReminder.isPending}>
                    {sendReminder.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Bell className="h-4 w-4" />
                    )}
                    <span className="ml-1">
                      {sendReminder.isPending ? t('common.saving') : t('invoices.send_reminder')}
                    </span>
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showPayLinkModal && (
            <Modal onClose={() => setShowPayLinkModal(false)} title={t('payments.send_pay_link')}>
              <div className="space-y-4">
                <p className={UI_MUTED}>
                  {t('payments.pay_link_desc')}
                </p>
                <div className="flex items-center gap-2 rounded-md border border-ds-line bg-ds-sunk px-3 py-2">
                  <span className={cn('flex-1 truncate', TABLE_CELL)}>
                    {payLinkUrl}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyPayLink}
                    className="shrink-0 rounded p-1 text-ds-muted hover:text-ds-text"
                  >
                    {payLinkCopied ? (
                      <Check className="h-4 w-4 text-ds-green-ink" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {payLinkCopied && (
                  <p className={`${TYPE.small} text-ds-green-ink`}>
                    {t('payments.link_copied')}
                  </p>
                )}
                <div className="flex justify-end">
                  <Button variant="secondary" onClick={() => setShowPayLinkModal(false)}>
                    {t('common.close')}
                  </Button>
                </div>
              </div>
            </Modal>
          )}

          {/* Sygeforsikring Danmark submit modal */}
          {showSdModal && id && activeClinicId && (
            <Modal
              title={t('sd.submit_modal_title')}
              onClose={() => {
                setShowSdModal(false);
                setSdValidation(null);
              }}
            >
              <div className="space-y-4">
                {validateInvoice.isPending && (
                  <div className={cn('flex items-center gap-2', UI_MUTED)}>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('sd.validating')}
                  </div>
                )}
                {sdValidation && (
                  <div
                    className={cn('rounded-lg p-4', TYPE.body, sdValidation.valid ? 'bg-ds-green-tint' : 'bg-ds-red-tint')}
                  >
                    <p
                      className={`font-medium ${sdValidation.valid ? 'text-ds-green-ink' : 'text-ds-red-ink'}`}
                    >
                      {sdValidation.valid ? t('sd.validation_ok') : t('sd.validation_errors')}
                    </p>
                    {sdValidation.errors.length > 0 && (
                      <ul className="mt-2 list-inside list-disc space-y-1 text-ds-red-ink">
                        {sdValidation.errors.map((e, i) => (
                          <li key={i}>
                            {e.field ? `${e.field}: ` : ''}
                            {e.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowSdModal(false);
                      setSdValidation(null);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    disabled={!sdValidation?.valid || submitInvoice.isPending}
                    onClick={async () => {
                      try {
                        const r = await submitInvoice.mutateAsync({
                          clinic_id: activeClinicId,
                          invoice_id: id,
                        });
                        toast.success(t('sd.submit_success', { uuid: r.uuid ?? '' }));
                        setShowSdModal(false);
                        setSdValidation(null);
                      } catch {
                        toast.error(t('sd.submit_error'));
                      }
                    }}
                  >
                    {submitInvoice.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {t('sd.confirm_submit')}
                  </Button>
                </div>
              </div>
            </Modal>
          )}

          {!isDraft && (
            <>
              <section className="rounded-card bg-ds-card shadow-card">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-6 py-4 text-left"
                  onClick={() => setShowEmailHistory((v) => !v)}
                >
                  <span className={cn(TYPE.card, 'text-ds-text')}>
                    {t('invoices.email_history')}
                    {emailHistory && emailHistory.length > 0 && (
                      <span className={cn('ml-2 rounded-full bg-ds-teal-tint px-s2 py-0.5 text-ds-teal', STATUS_BADGE)}>
                        {emailHistory.length}
                      </span>
                    )}
                  </span>
                  {showEmailHistory ? (
                    <ChevronUp className="h-4 w-4 text-ds-muted" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-ds-muted" />
                  )}
                </button>
                {showEmailHistory && (
                  <div className="border-t border-ds-line px-6 pb-4">
                    {!emailHistory || emailHistory.length === 0 ? (
                      <p className={cn('py-4 text-center', UI_MUTED)}>
                        {t('invoices.no_emails_yet')}
                      </p>
                    ) : (
                      <table className={cn('mt-2 min-w-full', TYPE.body)}>
                        <thead>
                          <tr className="border-b border-ds-line">
                            <Th>{t('invoices.field_issue_date', 'Date')}</Th>
                            <Th>{t('invoices.email_recipient_label')}</Th>
                            <Th>{t('invoices.col_status', 'Status')}</Th>
                            <Th>Message-ID</Th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-ds-line">
                          {emailHistory.map((row) => (
                            <tr key={row.id}>
                              <td className="py-2 pr-4 text-ds-muted">
                                {new Date(row.created_at).toLocaleString('da-DK')}
                              </td>
                              <td className="py-2 pr-4 text-ds-text">
                                {row.sent_to}
                              </td>
                              <td className="py-2 pr-4">
                                <span
                                  className={cn(
                                    'inline-block rounded-full px-2 py-0.5',
                                    STATUS_BADGE,
                                    row.status === 'sent'
                                      ? 'bg-ds-green-tint text-ds-green-ink'
                                      : 'bg-ds-red-tint text-ds-red-ink',
                                  )}
                                >
                                  {row.status}
                                </span>
                              </td>
                              <td className={cn('py-2 font-mono', UI_SECONDARY)}>
                                {row.postmark_message_id ?? '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </section>

              <section className="rounded-card bg-ds-card shadow-card">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-6 py-4 text-left"
                  onClick={() => setShowReminderHistory((v) => !v)}
                >
                  <span className={cn(TYPE.card, 'text-ds-text')}>
                    {t('invoices.reminder_history')}
                    {reminderHistory && reminderHistory.length > 0 && (
                      <span className={cn('ml-2 rounded-full bg-ds-amber-tint px-2 py-0.5 text-ds-amber-ink', STATUS_BADGE)}>
                        {reminderHistory.length}
                      </span>
                    )}
                  </span>
                  {showReminderHistory ? (
                    <ChevronUp className="h-4 w-4 text-ds-muted" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-ds-muted" />
                  )}
                </button>
                {showReminderHistory && (
                  <div className="border-t border-ds-line px-6 pb-4">
                    {!reminderHistory || reminderHistory.length === 0 ? (
                      <p className={cn('py-4 text-center', UI_MUTED)}>
                        {t('invoices.no_reminders_yet')}
                      </p>
                    ) : (
                      <table className={cn('mt-2 min-w-full', TYPE.body)}>
                        <thead>
                          <tr className="border-b border-ds-line">
                            <Th>{t('invoices.field_issue_date', 'Date')}</Th>
                            <Th>{t('invoices.reminder_number_label')}</Th>
                            <Th>{t('invoices.email_recipient_label')}</Th>
                            <Th>{t('invoices.days_overdue_label')}</Th>
                            <Th>{t('invoices.col_status', 'Status')}</Th>
                            <Th>{t('invoices.reminder_type_label')}</Th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-ds-line">
                          {reminderHistory.map((row) => (
                            <tr key={row.id}>
                              <td className="py-2 pr-4 text-ds-muted">
                                {new Date(row.created_at).toLocaleString('da-DK')}
                              </td>
                              <td className="py-2 pr-4 text-ds-text">
                                #{row.reminder_number}
                              </td>
                              <td className="py-2 pr-4 text-ds-text">
                                {row.sent_to}
                              </td>
                              <td className="py-2 pr-4 text-ds-muted">
                                {row.days_overdue} {t('invoices.days_label')}
                              </td>
                              <td className="py-2 pr-4">
                                <span
                                  className={cn(
                                    'inline-block rounded-full px-2 py-0.5',
                                    STATUS_BADGE,
                                    row.status === 'sent'
                                      ? 'bg-ds-green-tint text-ds-green-ink'
                                      : row.status === 'failed'
                                        ? 'bg-ds-red-tint text-ds-red-ink'
                                        : 'bg-ds-neutral-tint text-ds-muted',
                                  )}
                                >
                                  {row.status}
                                </span>
                              </td>
                              <td className="py-2 pr-4 text-ds-muted">
                                {row.is_manual ? t('invoices.manual_label') : t('invoices.automatic_label')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </section>
            </>
          )}

          {isVoid && invoice.voided_reason && (
            <p className={`${TYPE.body} text-ds-red-ink`}>
              {t('invoices.void_reason_label')}: {invoice.voided_reason}
            </p>
          )}
        </div>

      {showAppointmentPicker && isDraft && (
        <AppointmentLinePicker
          invoiceId={invoice.id}
          patientId={invoice.patient_id}
          clinicId={invoice.clinic_id}
          currency={invoice.currency}
          onClose={() => setShowAppointmentPicker(false)}
        />
      )}
    </PageLayout>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className={cn('px-3 py-3 text-left uppercase tracking-wide', TABLE_HEADER)}
    >
      {children}
    </th>
  );
}

function Row({
  label,
  bold,
  children,
}: {
  label: string;
  bold?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-center justify-between ${
        bold ? 'border-t border-ds-line pt-1 font-semibold' : ''
      }`}
    >
      <span className="text-ds-muted">{label}</span>
      <span className="text-ds-text">{children}</span>
    </div>
  );
}

function PatientFallback({ patientId }: { patientId: string }) {
  const { data: patient } = usePatient(patientId);
  if (!patient) return null;
  const telephones = formatPatientTelephoneNumbers(
    patient.telephone_subscribers,
    patient.phone,
  );
  return (
    <div className={`mt-1 ${UI_MUTED}`}>
      <div>
        {patient.first_name} {patient.last_name}
      </div>
      {telephones ? <div className={UI_SECONDARY}>{telephones}</div> : null}
    </div>
  );
}
